import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Route, Switch } from 'react-router-dom';
import { Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks';
import { AddIcon, ArchiveIcon, DialogueIcon, LibraryIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Empty, Heading3, Sidebar, SidebarLayout, Skeleton } from '@treams/treams-ui';

import FeedBackTemplateEditor, {
	FeedbackTemplateCreator,
} from '../../settings/feedback/feedback-templates/FeedBackTemplateEditor';
import FeedbackLibrary from './FeedbackLibrary';

export default function FeedbackSettings() {
	const { t } = useTranslation();

	const query = graphql.useFeedbackTemplatesQuery();
	const { localize } = useLocalize();

	const [archived, templates] = _.partition(
		_.sortBy(
			query.data?.feedbackTemplates.edges.map(({ node }) => node),
			[(template) => template.isArchived, (template) => localize(template.title).text],
		),
		(template) => template.isArchived,
	);

	return (
		<SidebarLayout
			sidebar={
				<Sidebar>
					<Sidebar.Item
						small
						icon={<LibraryIcon />}
						title={t('settings.feedbackLibrary.title')}
						exact
						path="/settings/feedback"
					/>
					<Sidebar.Item
						small
						icon={<ArchiveIcon />}
						title={t('settings.feedbackLibrary.archive.title')}
						exact
						path="/settings/feedback/archive"
					/>
					<Sidebar.Separator />
					<Heading3 mb={2}> {t('settings.feedbackLibrary.templates.title')}</Heading3>
					{query.data ? (
						<>
							{templates.map((template) => (
								<Sidebar.Item
									key={template.id}
									title={localize(template.title).text}
									small
									exact
									path={`/settings/feedback/${template.id}`}
								/>
							))}
							{templates.length === 0 && (
								<Skeleton.Empty
									contents={
										<Empty
											icon={<DialogueIcon />}
											title={t('settings.feedbackLibrary.templates.empty.title')}
											description={t('settings.feedbackLibrary.templates.empty.description')}
											small
										/>
									}
								>
									<Skeleton.Repeat count={3}>
										<Sidebar.Item.Skeleton small />
									</Skeleton.Repeat>
								</Skeleton.Empty>
							)}
							<Sidebar.Spacing />
							<Flex>
								<Button
									type="secondary"
									title={t('settings.feedbackLibrary.createTemplate.button')}
									icon={<AddIcon />}
									link="/settings/feedback/new"
								/>
							</Flex>
							{archived.length > 0 && (
								<>
									<Sidebar.Separator />
									<Heading3 mb={2}>{t('settings.feedbackLibrary.templates.archived')}</Heading3>
									{archived.map((template) => (
										<Sidebar.Item
											key={template.id}
											title={localize(template.title).text}
											small
											exact
											path={`/settings/feedback/${template.id}`}
										/>
									))}
								</>
							)}
						</>
					) : (
						<Skeleton.Repeat count={3}>
							<Sidebar.Item.Skeleton small />
						</Skeleton.Repeat>
					)}
				</Sidebar>
			}
		>
			<Switch>
				<Route path="/settings/feedback/archive">
					<FeedbackLibrary archived />
				</Route>
				<Route path="/settings/feedback/new">
					<FeedbackTemplateCreator />
				</Route>
				<Route path="/settings/feedback/:templateID">
					{({ match }) => <FeedBackTemplateEditor id={match!.params.templateID!} />}
				</Route>
				<Route>
					<FeedbackLibrary />
				</Route>
			</Switch>
		</SidebarLayout>
	);
}
